import { useLanguageStore } from '../store/languageStore.js';

// Kept in sync by hand with shared/constants.js's TRANSLATION_LANGUAGES —
// not imported directly since shared/constants.js is a CommonJS module and
// this is the one place in the client that needs the RTL flag/label
// alongside the code for rendering the switcher itself.
export const LANGUAGES = [
  { code: 'en', label: 'English', rtl: false },
  { code: 'zh', label: '中文 (Chinese)', rtl: false },
  { code: 'hi', label: 'हिन्दी (Hindi)', rtl: false },
  { code: 'es', label: 'Español (Spanish)', rtl: false },
  { code: 'ar', label: 'العربية (Arabic)', rtl: true },
  { code: 'fa', label: 'فارسی (Persian)', rtl: true },
  { code: 'ru', label: 'Русский (Russian)', rtl: false },
  { code: 'fr', label: 'Français (French)', rtl: false },
  { code: 'de', label: 'Deutsch (German)', rtl: false },
];
const RTL_CODES = new Set(LANGUAGES.filter((l) => l.rtl).map((l) => l.code));

/**
 * @returns {{
 *   language: string,
 *   setLanguage: (code: string) => void,
 *   isRtl: boolean,
 *   translate: (article: {title: string, summary: string, translations?: object}) => {title: string, summary: string, isTranslated: boolean}
 * }}
 *
 * `translate()` is the graceful-fallback contract this whole feature
 * depends on: if the current language is 'en', or the article simply
 * doesn't have a translation for the selected language (translation is now
 * manual/on-demand, triggered per-article per-language from Admin > Live
 * Activity / Articles — see
 * controllers/admin/articlesController.js::translateLanguage — so most
 * articles won't have most languages at any given moment), this returns the
 * article's original English title/summary rather than an empty string or
 * undefined. No component that renders an article's text should ever read
 * `article.title`/`article.summary` directly once a language other than
 * English is selected — always go through this so an untranslated article
 * never shows a blank card.
 *
 * The one deliberate EXCEPTION is the homepage feed itself
 * (hooks/useArticles.js's useInfiniteArticles + pages/Home.jsx): the server
 * already strictly filters that feed to only articles that actually have
 * the selected language (controllers/articleController.js's list()), so
 * this fallback simply never triggers there in practice — every article
 * useInfiniteArticles returns for a non-English language already has that
 * translation.
 */
export function useLanguage() {
  const language = useLanguageStore((s) => s.language);
  const setLanguage = useLanguageStore((s) => s.setLanguage);

  const translate = (article) => {
    if (language === 'en') return { title: article.title, summary: article.summary, isTranslated: false };
    const localized = article.translations?.[language];
    if (localized?.title && localized?.summary) {
      return { title: localized.title, summary: localized.summary, isTranslated: true };
    }
    // Not translated (yet, or ever) — fall back to English rather than
    // rendering nothing.
    return { title: article.title, summary: article.summary, isTranslated: false };
  };

  return { language, setLanguage, isRtl: RTL_CODES.has(language), translate };
}
